"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectPageDocument = projectPageDocument;
const fractional_indexing_1 = require("fractional-indexing");
const Y = __importStar(require("yjs"));
const page_document_maps_1 = require("./page-document-maps");
const page_format_chain_1 = require("./page-format-chain");
const page_lookup_1 = require("./page-lookup");
const page_read_source_1 = require("./page-read-source");
const page_structure_entry_1 = require("./page-structure-entry");
/** Stable field representation, including rich-text attributes and embeds. */
function valueOf(value) {
    if (value instanceof Y.Text)
        return { textDelta: valueOf(value.toDelta()) };
    if (value instanceof Y.Map)
        return valueOf(Object.fromEntries(value.entries()));
    if (value instanceof Y.Array)
        return valueOf(value.toArray());
    if (value instanceof Uint8Array)
        return { bytes: [...value] };
    if (Array.isArray(value))
        return value.map(valueOf);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value)
            .sort(([a], [b]) => compare(a, b))
            .map(([key, item]) => [key, valueOf(item)]));
    }
    return value;
}
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const idsOf = (value) => {
    const ids = value instanceof Y.Array ? value.toArray() : value;
    return Array.isArray(ids)
        ? ids.filter((id) => typeof id === 'string')
        : [];
};
const keysOf = (value) => value instanceof Y.Map
    ? [...value.keys()]
    : value && typeof value === 'object'
        ? Object.keys(value)
        : [];
function fieldsOf(fields, omitted) {
    return valueOf(Object.fromEntries([...fields].filter(([key]) => !omitted.includes(key))));
}
/** Content and ordering only: storage part IDs and fractional key spelling are not content. */
function projectPageDocument(source) {
    const version = (0, page_document_maps_1.readPageFormatVersion)(source.head);
    if (version === page_format_chain_1.PAGE_UPGRADE_STEPS[0].from)
        return projectChildLists(source.head);
    if (version !== page_format_chain_1.PAGE_UPGRADE_STEPS.at(-1).to)
        throw new Error(`Unsupported projection format: ${version}`);
    // A projection of the whole page reads every part.
    (0, page_read_source_1.assertPagePartsLoaded)(source.head, source.loadedPartIds());
    const { tree, blocks, elements } = (0, page_read_source_1.readPage)(source);
    const ids = (0, page_lookup_1.pagePlacedBlockIds)(tree);
    return {
        root: tree.root,
        blocks: ids.map(id => {
            const fields = blocks.get(id);
            if (!fields)
                throw new Error(`Missing block fields: ${id}`);
            return {
                id,
                fields: fieldsOf(fields, []),
                children: tree.children.get(id) ?? [],
            };
        }),
        elements: ids.flatMap(surface => (tree.elements.get(surface) ?? []).map(id => {
            const fields = elements.get(id);
            if (!fields)
                throw new Error(`Missing element fields: ${id}`);
            return { id, surface, fields: fieldsOf(fields, []) };
        })),
        members: [...tree.members].sort(([a], [b]) => compare(a, b)),
        presentation: tree.presentation,
    };
}
function projectChildLists(doc) {
    const blocks = (0, page_document_maps_1.pagePartBlocksMap)(doc);
    const listed = new Set();
    blocks.forEach(block => idsOf(block.get('sys:children')).forEach(id => listed.add(id)));
    const roots = [...blocks.keys()]
        .filter(id => !listed.has(id) && blocks.get(id).get('sys:flavour') === 'affine:page')
        .sort(compare);
    if (roots.length > 1)
        throw new Error('Multiple page roots cannot be projected without recovery');
    const root = roots[0] ?? null;
    const children = new Map();
    const layer = new Map();
    if (root)
        layer.set(root, (0, fractional_indexing_1.generateKeyBetween)(null, null));
    const seen = new Set();
    const pending = root ? [root] : [];
    seen.add(root ?? '');
    // The old format resolves duplicate references at the first reachable parent.
    for (const id of pending) {
        const placed = [];
        let order = null;
        for (const child of idsOf(blocks.get(id).get('sys:children'))) {
            order = (0, fractional_indexing_1.generateKeyBetween)(order, null);
            if (seen.has(child) || !blocks.has(child))
                continue;
            layer.set(child, order);
            seen.add(child);
            placed.push(child);
            pending.push(child);
        }
        children.set(id, placed);
    }
    const ordered = [];
    const stack = root ? [root] : [];
    while (stack.length) {
        const id = stack.pop();
        ordered.push(id);
        stack.push(...[...(children.get(id) ?? [])].reverse());
    }
    const result = {
        root,
        blocks: [],
        elements: [],
        members: [],
        presentation: [],
    };
    const groups = [];
    const frames = [];
    const mindmapShapes = new Set();
    const presentations = new Map();
    for (const id of ordered) {
        const block = blocks.get(id);
        const kind = block.get('sys:flavour');
        const omitted = ['sys:children'];
        if (kind === 'affine:surface') {
            omitted.push('prop:elements');
            const boxed = block.get('prop:elements');
            const elements = boxed instanceof Y.Map ? boxed.get('value') : null;
            if (elements instanceof Y.Map) {
                const entries = [...elements.entries()];
                for (const [elementId, fields] of entries) {
                    const order = fields instanceof Y.Map ? fields.get('index') : null;
                    if (!(0, page_structure_entry_1.isFractionalIndex)(order))
                        throw new Error(`Invalid element order: ${elementId}`);
                    layer.set(elementId, order);
                }
                entries.sort(([a], [b]) => compare(layer.get(a), layer.get(b)) || compare(a, b));
                for (const [elementId, fields] of entries) {
                    const type = fields.get('type');
                    const members = keysOf(fields.get('children'));
                    if (type === 'group')
                        groups.push([elementId, members]);
                    if (type === 'mindmap')
                        members.forEach(member => mindmapShapes.add(member));
                    result.elements.push({
                        id: elementId,
                        surface: id,
                        fields: fieldsOf(fields, type === 'group' ? ['index', 'children'] : ['index']),
                    });
                }
            }
        }
        if (kind === 'affine:frame') {
            omitted.push('prop:childElementIds', 'prop:presentationIndex');
            frames.push([id, keysOf(block.get('prop:childElementIds'))]);
            const order = block.get('prop:presentationIndex');
            if ((0, page_structure_entry_1.isFractionalIndex)(order))
                presentations.set(id, order);
        }
        result.blocks.push({
            id,
            fields: fieldsOf(block, omitted),
            children: children.get(id),
        });
    }
    const assigned = new Set();
    for (const [group, ids] of [...groups, ...frames]) {
        const members = ids
            .filter(id => {
            if (id === group ||
                !layer.has(id) ||
                assigned.has(id) ||
                mindmapShapes.has(id))
                return false;
            assigned.add(id);
            return true;
        })
            .sort((a, b) => compare(layer.get(a), layer.get(b)) || compare(a, b));
        if (members.length)
            result.members.push([group, members]);
    }
    result.members.sort(([a], [b]) => compare(a, b));
    result.presentation = [...presentations.keys()].sort((a, b) => compare(presentations.get(a), presentations.get(b)) || compare(a, b));
    return result;
}
